import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SuperHead } from "@/pages/super/SuperLayout";
import { useContent } from "@/context/ContentContext";
import { api, formatApiError } from "@/lib/api";

const HOME_GROUPS = [
  ["Hero", [["hero_eyebrow", "Small label"], ["hero_title", "Headline"], ["hero_highlight", "Word to highlight in headline"], ["hero_subtitle", "Intro text", true], ["cta_primary", "Main button"], ["cta_secondary", "Second button"], ["hero_image_a", "Large photo URL"], ["hero_image_b", "Small photo URL"]]],
  ["Browse by treatment", [["categories_eyebrow", "Small label"], ["categories_title", "Heading"]]],
  ["Popular clinics", [["popular_eyebrow", "Small label"], ["popular_title", "Heading"]]],
  ["Upcoming appointments", [["upcoming_eyebrow", "Small label"], ["upcoming_title", "Heading"], ["upcoming_subtitle", "Intro text", true]]],
  ["For clinics band", [["band_eyebrow", "Small label"], ["band_title", "Heading"], ["band_text", "Text", true]]],
  ["Footer", [["footer_tagline", "Tagline", true]]],
];
const SEO_PAGES = [["home", "Home page"], ["clinics", "Browse clinics"], ["clinic", "Clinic page"], ["book", "Booking page"], ["for_clinics", "For clinics"], ["pricing", "Pricing"], ["tour", "Product tour"], ["login", "Log in"], ["signup", "Sign up"]];

const TourEditor = ({ tour, setTour }) => {
  const steps = Object.keys(tour).filter((k) => /^step\d+_title$/.test(k)).map((k) => Number(k.match(/\d+/)[0])).sort((a, b) => a - b);
  const set = (k, v) => setTour({ ...tour, [k]: v });
  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-card border border-border p-6 grid sm:grid-cols-2 gap-4">
        <p className="font-display font-bold sm:col-span-2">Tour page intro</p>
        <Field k="tour_eyebrow" label="Small label" value={tour.tour_eyebrow} prefix="tour" onChange={set} />
        <Field k="tour_cta" label="Button text" value={tour.tour_cta} prefix="tour" onChange={set} />
        <Field k="tour_title" label="Headline" value={tour.tour_title} prefix="tour" onChange={set} />
        <Field k="tour_subtitle" label="Intro text" long value={tour.tour_subtitle} prefix="tour" onChange={set} />
      </div>
      {steps.map((i) => (
        <div key={i} className="rounded-2xl bg-card border border-border p-6 grid sm:grid-cols-[1fr_220px] gap-5" data-testid={`tour-editor-step-${i}`}>
          <div className="grid sm:grid-cols-2 gap-4">
            <p className="font-display font-bold sm:col-span-2">Step {i}</p>
            <Field k={`step${i}_tag`} label="Audience label" value={tour[`step${i}_tag`]} prefix="tour" onChange={set} />
            <Field k={`step${i}_title`} label="Title" value={tour[`step${i}_title`]} prefix="tour" onChange={set} />
            <Field k={`step${i}_text`} label="Description" long value={tour[`step${i}_text`]} prefix="tour" onChange={set} />
            <Field k={`step${i}_image`} label="Screenshot URL (https:// or /tour/…)" long value={tour[`step${i}_image`]} prefix="tour" onChange={set} />
          </div>
          <img src={tour[`step${i}_image`]} alt={`Step ${i}`} className="rounded-xl border border-border w-full aspect-[16/10] object-cover object-top self-center" />
        </div>
      ))}
    </div>
  );
};

const Field = ({ k, label, long, value, onChange, prefix }) => (
  <div className={`space-y-1.5 ${long ? "sm:col-span-2" : ""}`}>
    <Label>{label}</Label>
    {long ? <Textarea rows={3} value={value || ""} onChange={(e) => onChange(k, e.target.value)} data-testid={`${prefix}-${k}-input`} />
      : <Input value={value || ""} onChange={(e) => onChange(k, e.target.value)} data-testid={`${prefix}-${k}-input`} />}
  </div>
);

export default function SuperContent() {
  const { reload } = useContent();
  const [home, setHome] = useState(null);
  const [seo, setSeo] = useState(null);
  const [tour, setTour] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/content").then((r) => { setHome(r.data.home); setSeo(r.data.seo); setTour(r.data.tour); }); }, []);
  if (!home) return null;
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/super/content", { home, seo, tour }); setHome(data.home); setSeo(data.seo); setTour(data.tour); reload(); toast.success("Content published"); } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  return (
    <div className="max-w-4xl">
      <SuperHead eyebrow="CMS" title="Content & SEO">
        <Button variant="outline" className="rounded-full" asChild data-testid="content-preview-link"><a href="/" target="_blank" rel="noreferrer">View site</a></Button>
        <Button className="rounded-full" onClick={save} disabled={saving} data-testid="content-save-button">Publish changes</Button>
      </SuperHead>
      <Tabs defaultValue="home">
        <TabsList>
          <TabsTrigger value="home" data-testid="content-tab-home">Home page</TabsTrigger>
          <TabsTrigger value="seo" data-testid="content-tab-seo">SEO & page titles</TabsTrigger>
          <TabsTrigger value="tour" data-testid="content-tab-tour">Product tour</TabsTrigger>
        </TabsList>
        <TabsContent value="tour" className="mt-6"><TourEditor tour={tour} setTour={setTour} /></TabsContent>
        <TabsContent value="home" className="space-y-6 mt-6">
          {HOME_GROUPS.map(([title, fields]) => (
            <div key={title} className="rounded-2xl bg-card border border-border p-6">
              <p className="font-display font-bold mb-4">{title}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {fields.map(([k, l, long]) => <Field key={k} k={k} label={l} long={long} value={home[k]} prefix="content" onChange={(key, v) => setHome({ ...home, [key]: v })} />)}
              </div>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="seo" className="space-y-6 mt-6">
          <div className="rounded-2xl bg-card border border-border p-6 grid sm:grid-cols-2 gap-4">
            <Field k="site_name" label="Site name (fallback title)" value={seo.site_name} prefix="seo" onChange={(key, v) => setSeo({ ...seo, [key]: v })} />
            <p className="text-xs text-muted-foreground sm:col-span-2">On clinic and booking pages you can use {"{clinic}"} and {"{city}"} — they're replaced with the clinic's name and city.</p>
          </div>
          {SEO_PAGES.map(([p, label]) => (
            <div key={p} className="rounded-2xl bg-card border border-border p-6 space-y-4" data-testid={`seo-page-${p}`}>
              <p className="font-display font-bold">{label}</p>
              <Field k={`${p}_title`} label={`Page title (${(seo[`${p}_title`] || "").length}/60)`} value={seo[`${p}_title`]} prefix="seo" onChange={(key, v) => setSeo({ ...seo, [key]: v })} />
              <Field k={`${p}_description`} label={`Meta description (${(seo[`${p}_description`] || "").length}/160)`} long value={seo[`${p}_description`]} prefix="seo" onChange={(key, v) => setSeo({ ...seo, [key]: v })} />
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
